import React, { Dispatch, SetStateAction, useState } from 'react';
import {
  StyleSheet, ScrollView, Text, ActivityIndicator,
} from 'react-native';
import NotificationSettingRow from '../NotificationSettingRow';
import Colors from '../../constants/Colors';
import Button from '../Button';
import updateNotificationPreferences from '../../requests/Users/UpdateNotificationPreferences';
import AlertModal from '../AlertModal';
import Style from '../../constants/Style';
import { HappyScoreCategory, HappyScoreCategoryTitles } from '../../constants/Types';
import {
  NOTIFICATION_PREFERENCES_LOAD_FAILURE, NOTIFICATION_PREFERENCES_SAVE_FAILURE,
  NOTIFICATION_PREFERENCES_SAVE_SUCCESS,
} from '../../constants/AlertMessages';

interface NotificationPreferencesProps {
  energyEnabled: boolean,
  internalEnabled: boolean,
  externalEnabled: boolean,
  occupancyEnabled: boolean,
  amenitiesEnabled: boolean,
  itEnabled: boolean,
  ergonomicsEnabled: boolean,
  satisfactionEnabled: boolean,
  happyEnabled: boolean,
  setEnergyEnabled: Dispatch<SetStateAction<boolean>>,
  setInternalEnabled: Dispatch<SetStateAction<boolean>>,
  setExternalEnabled: Dispatch<SetStateAction<boolean>>,
  setOccupancyEnabled: Dispatch<SetStateAction<boolean>>,
  setAmenitiesEnabled: Dispatch<SetStateAction<boolean>>,
  setItEnabled: Dispatch<SetStateAction<boolean>>,
  setErgonomicsEnabled: Dispatch<SetStateAction<boolean>>,
  setSatisfactionEnabled: Dispatch<SetStateAction<boolean>>,
  setHappyEnabled: Dispatch<SetStateAction<boolean>>,
  preferencesLoaded: boolean,
}

const NotificationPreferences = ({
  energyEnabled, internalEnabled, externalEnabled, occupancyEnabled, amenitiesEnabled, itEnabled,
  ergonomicsEnabled, satisfactionEnabled, happyEnabled, setEnergyEnabled, setInternalEnabled,
  setExternalEnabled, setOccupancyEnabled, setAmenitiesEnabled, setItEnabled, setErgonomicsEnabled,
  setSatisfactionEnabled, setHappyEnabled, preferencesLoaded,
}: NotificationPreferencesProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);

  const showAlert = (title: string, message: string, error: boolean = true) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setLoading(false);
    setAlertVisible(true);
  };

  const save = () => {
    if (loading) return;
    setLoading(true);
    updateNotificationPreferences(
      energyEnabled,
      internalEnabled,
      externalEnabled,
      occupancyEnabled,
      amenitiesEnabled,
      itEnabled,
      ergonomicsEnabled,
      satisfactionEnabled,
      happyEnabled,
    ).then((res) => {
      if (res?.message) {
        showAlert(
          'Notification preferences saved successfully',
          NOTIFICATION_PREFERENCES_SAVE_SUCCESS,
          false,
        );
      } else showAlert('Error', res?.errorText ?? NOTIFICATION_PREFERENCES_SAVE_FAILURE);
    }).catch((error) => showAlert('Error', error?.message ?? NOTIFICATION_PREFERENCES_SAVE_FAILURE));
  };

  return preferencesLoaded === null ? <ActivityIndicator
    animating
    size='large'
    style={Style.activityIndicator}
  /> : <ScrollView
    style={styles.container}
    contentContainerStyle={styles.content}
    testID='notifications-list'
  >
    <Text style={styles.itemTitle}>
      {preferencesLoaded ? 'Building notifications' : NOTIFICATION_PREFERENCES_LOAD_FAILURE}
    </Text>
    {preferencesLoaded && <>
      <NotificationSettingRow
        title={HappyScoreCategoryTitles[HappyScoreCategory.ENERGY]}
        toggled={energyEnabled}
        toggle={setEnergyEnabled}
        testID='energy&waste-notifications-toggle'
      />
      <NotificationSettingRow
        title={HappyScoreCategoryTitles[HappyScoreCategory.INTERNAL_ENVIRONMENT]}
        toggled={internalEnabled}
        toggle={setInternalEnabled}
        testID='internal-env-notifications-toggle'
      />
      <NotificationSettingRow
        title={HappyScoreCategoryTitles[HappyScoreCategory.EXTERNAL_ENVIRONMENT]}
        toggled={externalEnabled}
        toggle={setExternalEnabled}
        testID='external-env-notifications-toggle'
      />
      <NotificationSettingRow
        title={HappyScoreCategoryTitles[HappyScoreCategory.OCCUPANCY]}
        toggled={occupancyEnabled}
        toggle={setOccupancyEnabled}
        testID='building-occupnacy-notifications-toggle'
      />
      <NotificationSettingRow
        title={HappyScoreCategoryTitles[HappyScoreCategory.AMENITIES]}
        toggled={amenitiesEnabled}
        toggle={setAmenitiesEnabled}
        testID='amenities-notifications-toggle'
      />
      <NotificationSettingRow
        title={HappyScoreCategoryTitles[HappyScoreCategory.IT]}
        toggled={itEnabled}
        toggle={setItEnabled}
        testID='it-notifications-toggle'
      />
      <NotificationSettingRow
        title={HappyScoreCategoryTitles[HappyScoreCategory.ERGONOMICS]}
        toggled={ergonomicsEnabled}
        toggle={setErgonomicsEnabled}
        testID='ergonomics-notifications-toggle'
      />
      <NotificationSettingRow
        title={HappyScoreCategoryTitles[HappyScoreCategory.SATISFACTION]}
        toggled={satisfactionEnabled}
        toggle={setSatisfactionEnabled}
        testID='occupant-satisfaction-notifications-toggle'
      />
      <NotificationSettingRow
        title='Happy Score'
        toggled={happyEnabled}
        toggle={setHappyEnabled}
        testID='happy-score-notifications-toggle'
      />
      <Button
        title='Save'
        affirmative
        onPress={save}
        disabled={loading}
        children={loading && <ActivityIndicator
          color='#FFF'
          size='small'
          animating={loading}
          style={styles.loadingIndicator}
        />}
        style={styles.saveButton}
      />
      <AlertModal
        modalVisible={alertVisible}
        setModalVisible={setAlertVisible}
        title={alertTitle}
        message={alertMessage}
        error={alertError}
      />
    </>}
  </ScrollView>;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 24,
  },
  itemTitle: {
    fontSize: 18,
    lineHeight: 27,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginBottom: 8,
  },
  saveButton: {
    alignSelf: 'flex-start',
    marginVertical: 32,
  },
  loadingIndicator: {
    marginStart: 12,
  },
});

export default NotificationPreferences;
